import moment from "moment";
import { DataTableItem } from "../../../../../components/Component";
import TableRow from "../table_components/TableRow";
import * as app_consts from "../../../../../constants/react_app_consts";
import { capitalizeWords, underscore_words } from "../../../../../utils/functionalUtils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  formatWeighbridgeWeightMt,
  resolveGrnNetWeight,
  resolveWeighbridgeNetWeight,
} from "../../../../../utils/weighbridgeUtils";

const PRODUCT_PREVIEW_LIMIT = 3;

const formatCompactText = (value, maxLength = 20, fallback = "---") => {
  if (!value) return fallback;

  const normalizedValue = String(value);
  return normalizedValue.length > maxLength ? `${normalizedValue.substring(0, maxLength)}...` : normalizedValue;
};

const collectProductNames = (value, names) => {
  if (!value) return;

  if (Array.isArray(value)) {
    value.forEach((item) => collectProductNames(item, names));
    return;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim();
    if (normalizedValue) {
      names.push(normalizedValue);
    }
    return;
  }

  if (typeof value === "object") {
    const productName =
      value?.product_name ||
      value?.product_label ||
      value?.product?.product_name ||
      value?.product?.product_label ||
      value?.product ||
      value?.name ||
      "";
    if (productName) {
      names.push(productName);
    }
  }
};

const dedupeProductNames = (names = []) =>
  [...new Set(names.map((name) => String(name || "").trim()).filter(Boolean))];

const getGoodsReceivedProductNames = (item = {}) =>
  dedupeProductNames([
    item?.product?.product_name,
    item?.product_name,
    ...(item?.grn_products || []),
    ...(item?.goods_received_products || []),
    ...(item?.goods_received?.goods_received_products || []),
    ...(item?.product_list || []),
  ].flatMap((value) => {
    const names = [];
    collectProductNames(value, names);
    return names;
  }));

const getDeliveryNoteProductNames = (item = {}) =>
  dedupeProductNames([
    ...(item?.product_list || []),
    ...(item?.loading_order?.products || []),
    ...(item?.delivery_note?.product_list || []),
    ...(item?.delivery_note_products || []),
  ].flatMap((value) => {
    const names = [];
    collectProductNames(value, names);
    return names;
  }));

const resolveTruckRegistration = (item = {}) =>
  item?.weigh_bridge_ticket?.truck_licence_plate ||
  item?.weigh_bridge_ticket?.truck_license_plate ||
  item?.truck_licence_plate ||
  item?.truck_license_plate ||
  item?.loading_order?.truck_license_plate ||
  item?.loading_order?.truck_licence_plate ||
  "---";

const getLoadingOrderProductNames = (item = {}) =>
  dedupeProductNames([
    ...(item?.products || []),
    ...(item?.loading_order?.products || []),
    ...(item?.loading_order_product_info?.products_list || []),
  ].flatMap((value) => {
    const names = [];
    collectProductNames(value, names);
    return names;
  }));

const getLoadingOrderProducts = (item = {}) =>
  item?.loading_order_product_info?.products_list || item?.products || item?.loading_order?.products || [];

const renderProductSummary = (productNames = []) => {
  if (productNames.length === 0) {
    return "---";
  }

  const visibleNames = productNames.slice(0, PRODUCT_PREVIEW_LIMIT);
  const hiddenCount = productNames.length - visibleNames.length;

  return (
    <div>
      {visibleNames.map((productName) => (
        <span key={productName} className="tb-sub d-block">
          {productName}
        </span>
      ))}
      {hiddenCount > 0 ? (
        <span className="tb-sub d-block">+{hiddenCount} more</span>
      ) : null}
    </div>
  );
};

const formatMetricTonnage = (value, fallback = "---") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const numericValue = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ""));

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return `${numericValue.toFixed(2)} MT`;
};

const resolveGoodsReceivedTonnage = (item = {}) =>
  formatWeighbridgeWeightMt(
    resolveGrnNetWeight(
      item?.weigh_bridge_ticket?.net_weight,
      item?.weigh_bridge_ticket?.first_weight,
      item?.weigh_bridge_ticket?.second_weight,
    ),
    { fallback: "---" },
  );

const resolveDeliveryNoteTonnage = (item = {}) =>
  formatWeighbridgeWeightMt(
    resolveWeighbridgeNetWeight(
      item?.weigh_bridge_ticket?.net_weight,
      item?.weigh_bridge_ticket?.first_weight,
      item?.weigh_bridge_ticket?.second_weight,
    ),
    { fallback: "---" },
  );

const resolveLoadingOrderTonnage = (item = {}) => {
  const fallbackProductTonnage = getLoadingOrderProducts(item).reduce(
    (sum, product) => sum + (Number.parseFloat(product?.total_mt_capacity) || 0),
    0,
  );

  const totalTonnage =
    item?.loading_order?.total_tonnage ??
    item?.total_tonnage ??
    (fallbackProductTonnage > 0 ? fallbackProductTonnage : "");

  return formatMetricTonnage(totalTonnage);
};

const formatBranchReferenceValue = (value) => {
  if (Array.isArray(value)) {
    const filteredValues = value.filter(Boolean);
    return filteredValues.length > 0 ? filteredValues.join(", ") : "--";
  }

  return value || "--";
};

const shouldIgnoreRowClick = (target) => {
  if (!target || typeof target.closest !== "function") return false;

  return Boolean(
    target.closest(
      "a, button, input, select, textarea, label, .nk-tb-col-tools, .dropdown-toggle, .dropdown-menu, .nav-link",
    ),
  );
};

const ClickableDataTableItem = ({ rowLink, className, children, ...props }) => {
  const navigate = useNavigate();
  const isClickable = Boolean(rowLink);

  const handleNavigate = () => {
    if (isClickable) {
      navigate(rowLink);
    }
  };

  const handleClick = (event) => {
    if (!isClickable || shouldIgnoreRowClick(event.target)) return;
    handleNavigate();
  };

  const handleKeyDown = (event) => {
    if (!isClickable) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleNavigate();
  };

  return (
    <DataTableItem
      className={`${className ? className : ""}${isClickable ? " cursor-pointer" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? "link" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      {...props}
    >
      {children}
    </DataTableItem>
  );
};

export const UserListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  editFunction,
  setOpendModalDetails,
  setSelectedData,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem key={item.member_access_id} rowLink={`/dashboard/user/${item.phone_number}`}>
                <TableRow rowLink={`/dashboard/user/${item.phone_number}`} item={item} avatar type="rowMain" />
                <TableRow item={item.phone_number} type="rowNormal" />
                <TableRow item={item.email_address} type="rowNormal" />
                <TableRow
                  item={item.account_status}
                  type="rowStatus"
                  item_color={
                    item.account_status_id &&
                    (item.account_status_id === 1
                      ? "success"
                      : item.account_status_id === 2
                        ? "warning"
                        : item.account_status_id === 3
                          ? "danger"
                          : "")
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  view_details
                  amendFunc={amendFunction}
                  editFunc={editFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Branch Location Rows
export const BranchLocationRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item.branch_location_id}
                rowLink={`/dashboard/admin/branch_locations/${item?.branch_location_id}/${underscore_words(item?.branch_area)}`}
              >
                <TableRow
                  rowLink={`/dashboard/admin/branch_locations/${item?.branch_location_id}/${underscore_words(item?.branch_area)}`}
                  item={item}
                  table_type={type}
                  type="rowMain"
                />

                <TableRow
                  item={formatBranchReferenceValue(item?.branch_reference_no || item?.branch_ref_no)}
                  type="rowNormal"
                />

                <TableRow
                  item={
                    item.branch_physical_address
                      ? item.branch_physical_address.length > 20
                        ? `${item.branch_physical_address.substring(0, 20)}...`
                        : item.branch_physical_address
                      : "--"
                  }
                  type="rowNormal"
                />

                <TableRow item={item.branch_mobile_number || "--"} type="rowNormal" />
                <TableRow item={item.province_name || "--"} type="rowNormal" />

                <TableRow
                  item={item.account_status}
                  type="rowStatus"
                  item_color={
                    item.account_status_id &&
                    (item.account_status_id === 1
                      ? "success"
                      : item.account_status_id === 2
                        ? "warning"
                        : item.account_status_id === 3
                          ? "danger"
                          : "")
                  }
                />

                <TableRow
                  item={item}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  table_type={type}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};
export const ProductRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem key={item.branch_location_id} rowLink={`/dashboard/admin/products/${item?.product_id}`}>
                <TableRow item={item} rowLink={`/dashboard/admin/products/${item?.product_id}`} type="rowMain" table_type={type} />
                <TableRow
                  item={
                    item.product_type
                      ? item.product_type.length > 20
                        ? `${item.product_type.substring(0, 20)}...`
                        : item.product_type
                      : ""
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={
                    item?.pum_list
                      ? item?.pum_list?.length == 1
                        ? item?.pum_list[0]?.unit_measurement_abbrev
                        : item?.pum_list?.length > 1
                          ? `${item?.pum_list[0]?.unit_measurement_abbrev}, ${item?.pum_list[1]?.unit_measurement_abbrev}`
                          : ""
                      : ""
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item.account_status}
                  type="rowStatus"
                  item_color={
                    item.account_status_id &&
                    (item.account_status_id === 1
                      ? "success"
                      : item.account_status_id === 2
                        ? "warning"
                        : item.account_status_id === 3
                          ? "danger"
                          : "")
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  table_type={type}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

export const ProductTypeRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <DataTableItem key={item.branch_location_id}>
                <TableRow item={item} table_type={type} type="rowMain" />
                <TableRow item={item.products_count} type="rowNormal" />
                <TableRow
                  item={item.account_status}
                  type="rowStatus"
                  item_color={
                    item.account_status_id &&
                    (item.account_status_id === 1
                      ? "success"
                      : item.account_status_id === 2
                        ? "warning"
                        : item.account_status_id === 3
                          ? "danger"
                          : "")
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </DataTableItem>
            </>
          );
        })}
    </>
  );
};

export const UnitMeasurementRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <DataTableItem key={item.unit_measurement_id}>
                <TableRow item={item} type="rowMain" table_type={type} />
                <TableRow item={item.unit_measurement_abbrev} type="rowNormal" />
                <TableRow
                  item={item.account_status}
                  type="rowStatus"
                  item_color={
                    item.account_status_id &&
                    (item.account_status_id === 1
                      ? "success"
                      : item.account_status_id === 2
                        ? "warning"
                        : item.account_status_id === 3
                          ? "danger"
                          : "")
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </DataTableItem>
            </>
          );
        })}
    </>
  );
};

// Employee List Rows
export const EmployeeListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <DataTableItem key={item?.employee?.employee_id}>
                <TableRow item={item} type="rowMain" table_type={type} />
                <TableRow item={`${item?.user?.first_name} ${item?.user?.surname}`} type="rowNormal" />
                <TableRow item={item?.employee_role?.role_name} type="rowNormal" />
                <TableRow item={item?.employee_department?.department_name.toUpperCase()} type="rowNormal" />
                <TableRow item={item?.branch_location?.branch_area} type="rowNormal" />
                <TableRow
                  item={item?.user_account?.account_status}
                  type="rowStatus"
                  item_color={
                    item?.user_account?.account_status_id &&
                    (item?.user_account?.account_status_id === 1
                      ? "success"
                      : item?.user_account?.account_status_id === 2
                        ? "warning"
                        : item?.user_account?.account_status_id === 3
                          ? "danger"
                          : "")
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </DataTableItem>
            </>
          );
        })}
    </>
  );
};

// Goods Received Weigh Bridge Ticket List Rows
export const GoodsReceivedWeighBridgeTicketListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  const [table_data, setTableData] = useState(data);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  return (
    <>
      {table_data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.goods_received?.goods_received_ref_number}
                rowLink={`/dashboard/goods_received/${item?.goods_received?.goods_received_ref_number}`}
              >
                <TableRow
                  rowLink={`/dashboard/goods_received/${item?.goods_received?.goods_received_ref_number}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow
                  item={
                    item.user_details?.name
                      ? item.user_details?.name.length > 20
                        ? `${item.user_details?.name.substring(0, 20)}...`
                        : item.user_details?.name
                      : "---"
                  }
                  type="rowNormal"
                />
                <TableRow item={item?.user_account?.unique_identification_number || "--"} type="rowNormal" />
                <TableRow item={renderProductSummary(getGoodsReceivedProductNames(item))} type="rowNormal" />
                <TableRow
                  item={item?.weigh_bridge_ticket_type?.weigh_bridge_ticket_type.toUpperCase() || "--"}
                  type="rowNormal"
                />
                <TableRow item={resolveTruckRegistration(item)} type="rowNormal" />
                <TableRow item={resolveGoodsReceivedTonnage(item)} type="rowNormal" />
                <TableRow
                  item={
                    item?.branch_location?.branch_area
                      ? item.branch_location?.branch_area.length > 20
                        ? `${item.branch_location?.branch_area.substring(0, 20)}...`
                        : item.branch_location?.branch_area
                      : "---"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={
                    item?.goods_received?.inserted_at
                      ? moment(item?.goods_received?.inserted_at).format("YYYY-MM-DD h:mm:ss")
                      : "--"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status || "--"}
                  type="rowStatus"
                  item_color={
                    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ===
                    app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_COMPLETE
                      ? "success"
                      : "warning"
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Smart Invoice List Rows
export const SmartInvoiceListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  const formatMoney = (value) => {
    const num = Number(value);
    return Number.isFinite(num)
      ? num.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  const normalizeStatus = (status = "") => String(status).trim().toLowerCase();

  const getStatusColor = (status = "") => {
    const s = normalizeStatus(status);

    if (s === "validated") return "success";
    if (s === "failed") return "danger";
    if (s === "draft") return "secondary";
    if (s === "refunded") return "info";
    if (s === "pending" || s === "pending_retry") return "warning";

    return "secondary";
  };

  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.id || item?.invoice_number}
                rowLink={`/dashboard/smart_invoices/${item?.id || item?.invoice_number}`}
              >
                <TableRow
                  rowLink={`/dashboard/smart_invoices/${item?.id || item?.invoice_number}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />

                <TableRow
                  item={
                    item?.customer_name
                      ? item.customer_name.length > 20
                        ? `${item.customer_name.substring(0, 20)}...`
                        : item.customer_name
                      : "---"
                  }
                  type="rowNormal"
                />

                <TableRow item={item?.customer_tpin || "---"} type="rowNormal" />

                <TableRow item={item?.delivery_note_ref || "---"} type="rowNormal" />

                <TableRow item={`ZMW ${formatMoney(item?.total_amount)}`} type="rowNormal" />

                <TableRow item={item?.status || "---"} type="rowStatus" item_color={getStatusColor(item?.status)} />

                <TableRow
                  item={
                    item?.issued_at
                      ? moment(item?.issued_at).format("YYYY-MM-DD h:mm:ss")
                      : item?.invoice_date
                        ? moment(item?.invoice_date).format("YYYY-MM-DD")
                        : "--"
                  }
                  type="rowNormal"
                />

                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Goods Received Weigh Bridge Ticket List Rows
export const WarehouseInventoryListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatMt = (v) => `${toNum(v).toFixed(2)} MT`;

  return (
    <>
      {Array.isArray(data) &&
        data.length > 0 &&
        currentItems?.map((item) => {
          const physical = toNum(item?.sum_total_physical_in_mt);
          const total = Number.isFinite(Number(item?.total_mt)) ? toNum(item?.total_mt) : physical;

          const status = item?.stock_status || "---";
          const statusColor = status.toLowerCase().includes("negative")
            ? "danger"
            : status.toLowerCase().includes("zero")
              ? "warning"
              : "success";

          const mainItem = {
            ...item,
            product_name: item?.product_name || "---",
            product_type: item?.product_type || "---",
            total_mt_display: formatMt(total),
          };

          return (
            <ClickableDataTableItem key={item?.product_id} rowLink={`/dashboard/warehouse_inventory/${item?.product_id}`}>
              <TableRow
                rowLink={`/dashboard/warehouse_inventory/${item?.product_id}`}
                item={mainItem}
                type="rowMain"
                table_type={type}
              />

              <TableRow
                item={
                  item?.product_type
                    ? item.product_type.length > 20
                      ? `${item.product_type.substring(0, 20)}...`
                      : item.product_type
                    : "---"
                }
                type="rowNormal"
              />

              <TableRow item={formatMt(physical)} type="rowNormal" />
              <TableRow item={formatMt(total)} type="rowNormal" />

              <TableRow item={status} type="rowStatus" item_color={statusColor} />

              <TableRow
                item={item}
                table_type={type}
                type="rowOptions"
                rowOptData={rowOptionsData}
                amendFunc={amendFunction}
                setOpendModalDetails={setOpendModalDetails}
                setSelectedData={setSelectedData}
              />
            </ClickableDataTableItem>
          );
        })}
    </>
  );
};

// Goods Received Weigh Bridge Ticket List Rows
export const DeliveryNoteTicketListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.delivery_note?.delivery_note_ref_number}
                rowLink={`/dashboard/delivery_notes/${item?.delivery_note?.delivery_note_ref_number}`}
              >
                <TableRow
                  rowLink={`/dashboard/delivery_notes/${item?.delivery_note?.delivery_note_ref_number}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow
                  item={
                    item.user_details?.name
                      ? item.user_details?.name.length > 20
                        ? `${item.user_details?.name.substring(0, 20)}...`
                        : item.user_details?.name
                      : "---"
                  }
                  type="rowNormal"
                />
                <TableRow item={item?.user_account?.unique_identification_number || "--"} type="rowNormal" />
                <TableRow item={renderProductSummary(getDeliveryNoteProductNames(item))} type="rowNormal" />
                <TableRow
                  item={item?.weigh_bridge_ticket_type?.weigh_bridge_ticket_type.toUpperCase() || "--"}
                  type="rowNormal"
                />
                <TableRow
                  item={
                    item.delivery_note?.delivery_location
                      ? item?.delivery_note?.delivery_location.length > 20
                        ? `${item?.delivery_note?.delivery_location?.substring(0, 20)}...`
                        : item?.delivery_note?.delivery_location
                      : ""
                  }
                  type="rowNormal"
                />
                <TableRow item={resolveTruckRegistration(item)} type="rowNormal" />
                <TableRow item={resolveDeliveryNoteTonnage(item)} type="rowNormal" />
                <TableRow
                  item={
                    item?.delivery_note?.created_date
                      ? moment(item?.delivery_note?.created_date).format("YYYY-MM-DD h:mm:ss")
                      : "--"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status || "--"}
                  type="rowStatus"
                  item_color={
                    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ===
                      app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_COMPLETE ||
                    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ===
                      app_consts.WEIGH_BRIDGE_TICKET_DELIVERED
                      ? "success"
                      : "warning"
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Client Goods Received List Rows
export const ClientGoodsReceivedListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item) => {
          return (
            <ClickableDataTableItem
              key={item?.goods_received?.goods_received_ref_number}
              rowLink={`/customer/goods_received/${item?.goods_received?.goods_received_ref_number}`}
            >
              <TableRow
                rowLink={`/customer/goods_received/${item?.goods_received?.goods_received_ref_number}`}
                item={item}
                type="rowMain"
                table_type={type}
              />

              <TableRow item={formatCompactText(item?.consigner?.name)} type="rowNormal" />

              <TableRow item={item?.consigner?.unique_identification_number || "---"} type="rowNormal" />

              <TableRow item={renderProductSummary(getGoodsReceivedProductNames(item))} type="rowNormal" />

              <TableRow item={item?.weigh_bridge_ticket?.weigh_bridge_ticket_reference_no || "---"} type="rowNormal" />

              <TableRow item={resolveTruckRegistration(item)} type="rowNormal" />

              <TableRow item={resolveGoodsReceivedTonnage(item)} type="rowNormal" />

              <TableRow
                item={
                  item?.goods_received?.inserted_at
                    ? moment(item?.goods_received?.inserted_at).format("YYYY-MM-DD h:mm:ss")
                    : "--"
                }
                type="rowNormal"
              />

              <TableRow
                item={item?.weigh_bridge_ticket?.weigh_bridge_ticket_status || "---"}
                type="rowStatus"
                item_color={
                  item?.weigh_bridge_ticket?.weigh_bridge_ticket_status_id ===
                  app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_COMPLETE
                    ? "success"
                    : "warning"
                }
              />

              <TableRow
                item={item}
                table_type={type}
                type="rowOptions"
                rowOptData={rowOptionsData}
                amendFunc={amendFunction}
                setOpendModalDetails={setOpendModalDetails}
                setSelectedData={setSelectedData}
              />
            </ClickableDataTableItem>
          );
        })}
    </>
  );
};

// Customer User Account List Rows
export const CustomerUserAccountListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.user_account?.unique_identification_number}
                rowLink={`/dashboard/user/${item?.user?.phone_number || item?.user_account?.username}`}
              >
                <TableRow
                  rowLink={`/dashboard/user/${item?.user?.phone_number || item?.user_account?.username}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow item={item?.user?.first_name} type="rowNormal" />
                <TableRow item={item?.user?.surname} type="rowNormal" />
                <TableRow item="Customer" type="rowNormal" />
                <TableRow
                  item={
                    item?.user_account?.date_created
                      ? moment(item?.user_account?.date_created).format("YYYY-MM-DD h:mm:ss")
                      : "--"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item?.account_status?.account_status}
                  type="rowStatus"
                  item_color={
                    item?.account_status?.account_status_id === app_consts.ACTIVATE_ACCOUNT_ID ? "success" : "warning"
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Customer User Account List Rows
export const ClientDeliveryNoteListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.delivery_note?.delivery_note_ref_number}
                rowLink={`/customer/delivery_notes/${item?.delivery_note?.delivery_note_ref_number}`}
              >
                <TableRow
                  rowLink={`/customer/delivery_notes/${item?.delivery_note?.delivery_note_ref_number}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow item={formatCompactText(item?.delivery_note?.delivery_location)} type="rowNormal" />
                <TableRow item={renderProductSummary(getDeliveryNoteProductNames(item))} type="rowNormal" />
                <TableRow item={item?.weigh_bridge_ticket?.weigh_bridge_ticket_reference_no} type="rowNormal" />
                <TableRow item={resolveTruckRegistration(item)?.toUpperCase?.() || resolveTruckRegistration(item)} type="rowNormal" />
                <TableRow item={resolveDeliveryNoteTonnage(item)} type="rowNormal" />
                <TableRow
                  item={
                    item?.delivery_note?.created_date
                      ? moment(item?.delivery_note?.created_date).format("YYYY-MM-DD h:mm:ss")
                      : "--"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status}
                  type="rowStatus"
                  item_color={
                    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ===
                      app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_COMPLETE ||
                    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ===
                      app_consts.WEIGH_BRIDGE_TICKET_DELIVERED
                      ? "success"
                      : "warning"
                  }
                />
                {/* Options */}
                {/* <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                /> */}
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Company User Account List Rows
export const CompanyUserAccountListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.user_account?.unique_identification_number}
                rowLink={`/dashboard/user/accounts/company/${item?.user_account?.username}`}
              >
                <TableRow
                  rowLink={`/dashboard/user/accounts/company/${item?.user_account?.username}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow item={item?.company?.company_name} type="rowNormal" />
                <TableRow item={item?.company?.company_email_address} type="rowNormal" />
                <TableRow item={item?.company?.company_mobile_number} type="rowNormal" />
                <TableRow item="Company" type="rowNormal" />
                <TableRow
                  item={
                    item?.user_account?.date_created
                      ? moment(item?.user_account?.date_created).format("YYYY-MM-DD h:mm:ss")
                      : "--"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item?.account_status?.account_status}
                  type="rowStatus"
                  item_color={
                    item?.account_status?.account_status_id === app_consts.ACTIVATE_ACCOUNT_ID ? "success" : "warning"
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Truck Driver User Account List Rows
export const TruckDriverUserAccountListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.user_account?.unique_identification_number}
                rowLink={`/dashboard/user/${item?.user?.phone_number || item?.user_account?.username}`}
              >
                <TableRow
                  rowLink={`/dashboard/user/${item?.user?.phone_number || item?.user_account?.username}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow item={item?.user?.first_name} type="rowNormal" />
                <TableRow item={item?.user?.surname} type="rowNormal" />
                <TableRow item="Truck Driver" type="rowNormal" />
                <TableRow
                  item={
                    item?.user_account?.date_created
                      ? moment(item?.user_account?.date_created).format("YYYY-MM-DD h:mm:ss")
                      : "--"
                  }
                  type="rowNormal"
                />
                <TableRow
                  item={item?.account_status?.account_status}
                  type="rowStatus"
                  item_color={
                    item?.account_status?.account_status_id === app_consts.ACTIVATE_ACCOUNT_ID ? "success" : "warning"
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};

// Employee Departments and Roles List Rows
export const EmpDeptRolesListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.employee_department?.department_id}
                rowLink={`/dashboard/admin/departments_and_roles/${item?.employee_role?.role_id}/${underscore_words(item?.employee_role?.role_name)}`}
              >
                <TableRow
                  rowLink={`/dashboard/admin/departments_and_roles/${item?.employee_role?.role_id}/${underscore_words(item?.employee_role?.role_name)}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow item={item?.employee_department?.department_name?.toUpperCase()} type="rowNormal" />
                <TableRow item={item?.num_emp} type="rowNormal" />
                <TableRow item={item?.num_actions} type="rowNormal" />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};
// Employee Departments and Roles List Rows
export const LoadingOrderListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem
                key={item?.loading_order_ref_no}
                rowLink={`/dashboard/loading_orders/${item?.loading_order_ref_no}`}
              >
                <TableRow
                  rowLink={`/dashboard/loading_orders/${item?.loading_order_ref_no}`}
                  item={item}
                  type="rowMain"
                  table_type={type}
                />
                <TableRow item={item?.owner || "---"} type="rowNormal" />
                <TableRow item={item?.contact_person || "---"} type="rowNormal" />
                <TableRow item={item?.contact_mobile || "---"} type="rowNormal" />
                <TableRow item={item?.total_products ?? item?.loading_order?.total_products ?? getLoadingOrderProducts(item).length} type="rowNormal" />
                <TableRow item={renderProductSummary(getLoadingOrderProductNames(item))} type="rowNormal" />
                <TableRow item={resolveTruckRegistration(item)} type="rowNormal" />
                <TableRow item={resolveLoadingOrderTonnage(item)} type="rowNormal" />
                <TableRow
                  item={item?.loading_order_type ? capitalizeWords(item?.loading_order_type) : "---"}
                  type="rowNormal"
                />
                <TableRow
                  item={item?.status || "---"}
                  type="rowStatus"
                  item_color={
                    item?.status?.toLowerCase() === app_consts.PROCESSING_COMPLETE?.toLowerCase()
                      ? "success"
                      : "warning"
                  }
                />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};
// Employee Departments and Roles List Rows
export const GrnSchedulerListRows = ({
  data,
  currentItems,
  rowOptionsData,
  amendFunction,
  setOpendModalDetails,
  setSelectedData,
  type,
}) => {
  return (
    <>
      {data.length > 0 &&
        currentItems?.map((item, index) => {
          return (
            <>
              <ClickableDataTableItem key={item?.grn_schdeuler?.name} rowLink={`/dashboard/grn_schedule/${item?.grn_schdeuler?.name}`}>
                <TableRow item={item?.grn_schdeuler?.name || "---"} rowLink={`/dashboard/grn_schedule/${item?.grn_schdeuler?.name}`} type="rowMain" />
                <TableRow item={item?.grn_schdeuler?.count || "---"} type="rowNormal" />
                {/* Options */}
                <TableRow
                  item={item}
                  table_type={type}
                  type="rowOptions"
                  rowOptData={rowOptionsData}
                  amendFunc={amendFunction}
                  setOpendModalDetails={setOpendModalDetails}
                  setSelectedData={setSelectedData}
                />
              </ClickableDataTableItem>
            </>
          );
        })}
    </>
  );
};
